import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, AlertCircle, RefreshCw } from "lucide-react";

export const SetupChecklist = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.get("/admin/setup-status");
      setData(res);
    } catch {
      setData(null);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <p className="text-sm text-muted-foreground">Checking your setup…</p>;

  const pending = data.items.filter((i) => !i.ok);
  const done = data.items.filter((i) => i.ok);
  const pct = Math.round((data.ready / data.total) * 100);

  const Row = ({ item }) => (
    <div
      className={`flex items-start gap-3 rounded-xl border p-4 transition-colors ${
        item.ok ? "border-border bg-muted/20" : "border-secondary/40 bg-secondary/5"}`}
      data-testid={`setup-item-${item.key}`}
    >
      {item.ok
        ? <CheckCircle2 size={18} className="text-primary shrink-0 mt-0.5"/>
        : <AlertCircle size={18} className="text-secondary shrink-0 mt-0.5"/>}
      <div className="min-w-0">
        <div className="font-medium text-sm">{item.label}</div>
        <div className="text-xs text-muted-foreground mt-0.5 break-words">{item.detail}</div>
        {!item.ok && (
          <div className="text-xs mt-2">
            <span className="text-foreground/80">{item.action}</span>
            {item.where && <span className="text-muted-foreground"> · {item.where}</span>}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Card className="border-border" data-testid="setup-checklist">
      <CardContent className="p-6 lg:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className="font-display text-2xl">Go-live checklist</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Everything your practice needs before real customers arrive — checked live, not from a saved flag.
            </p>
          </div>
          <Button variant="outline" onClick={load} disabled={busy} className="btn-pill" data-testid="setup-refresh">
            <RefreshCw size={14} className={`mr-1.5 ${busy ? "animate-spin" : ""}`}/> Re-check
          </Button>
        </div>

        <div className="mt-6 mb-7">
          <div className="flex items-end justify-between mb-2">
            <span className="font-display text-4xl" data-testid="setup-score">{data.ready}<span className="text-muted-foreground text-2xl">/{data.total}</span></span>
            <span className="text-xs uppercase tracking-wider text-muted-foreground">{pct}% ready</span>
          </div>
          <div className="h-2 rounded-full bg-border overflow-hidden">
            <div className="h-full bg-primary transition-all duration-700" style={{ width: `${pct}%` }}/>
          </div>
        </div>

        {pending.length > 0 && (
          <>
            <div className="overline mb-3">Needs your attention ({pending.length})</div>
            <div className="grid md:grid-cols-2 gap-3 mb-7">
              {pending.map((i) => <Row key={i.key} item={i}/>)}
            </div>
          </>
        )}

        <div className="overline mb-3">Ready ({done.length})</div>
        <div className="grid md:grid-cols-2 gap-3">
          {done.map((i) => <Row key={i.key} item={i}/>)}
        </div>
      </CardContent>
    </Card>
  );
};

export default SetupChecklist;
